// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/**
 * @license
 * Copyright 2024 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Color variables for the screenshot */
.neuroglancer-screenshot-overlay {
  --gray300: #d0d5dd;
  --gray600: #141415;
  --gray500: #f7f7f7;
  --gray50: #e6e6e6;
  --gray800: #344054;
  --gray700: rgba(20, 20, 21, 0.6);
  --gray400: rgba(20, 20, 21, 0.4);
  --gray200: rgba(20, 20, 21, 0.8);
  --primary500: #0069eb;
  --primary700: #0474ff;
}

/* General headings, labels, and top-level containers */
.neuroglancer-screenshot-overlay :is(div, table, tr, td, th) {
  box-sizing: border-box;
  outline: 0;
}

.neuroglancer-screenshot-dialog {
  width: 48.75rem;
  padding: 0;
  margin: 1.25rem auto;
  position: relative;
  transform: none;
  left: auto;
  top: auto;
  border-radius: 0.5rem;
  font-family: sans-serif;
}

.neuroglancer-screenshot-main-body-container {
  height: auto;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
}

.neuroglancer-screenshot-title {
  font-size: 0.938rem;
  font-weight: 590;
  color: var(--gray600);
  margin: 0;
}

.neuroglancer-screenshot-title-subheading {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.neuroglancer-screenshot-label {
  font-size: 0.813rem;
  color: var(--gray200);
  font-weight: 590;
}

/* Div at the top which contains the close */
.neuroglancer-screenshot-close {
  border-bottom: 1px solid var(--gray50);
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  gap: 10px;
}

/* Filename input menu */
.neuroglancer-screenshot-filename-container {
  padding: 1rem 1rem 0.75rem 1rem;
}

.neuroglancer-screenshot-name-label {
  color: var(--gray800);
  font-style: normal;
  display: block;
  margin: 0.75rem 0 0.375rem;
}

.neuroglancer-screenshot-name-input {
  width: 100%;
  margin-right: 10px;
  border-radius: 8px;
  border: 1px solid var(--gray300);
  background: white;
  font-size: 0.813rem;
  font-style: normal;
  padding: 8px 12px;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  color: var(--gray700);
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  line-height: 20px;
  box-sizing: border-box;
  outline: 0;
}

.neuroglancer-screenshot-name-input:disabled {
  background: var(--gray500);
  color: var(--gray400);
}

.neuroglancer-screenshot-name-input::placeholder {
  color: var(--gray700);
}

/* Scale selection menu */
.neuroglancer-screenshot-scale-factor-label {
  width: 100%;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.15rem;
}

.neuroglancer-screenshot-scale-menu {
  padding: 0 1rem;
  display: flex;
  flex-wrap: wrap;
}

.neuroglancer-screenshot-scale-radio-container {
  display: flex;
  flex-direction: row;
}

.neuroglancer-screenshot-scale-radio-item {
  margin-right: 2.125rem;
}

.neuroglancer-screenshot-scale-radio-label {
  color: var(--gray700);
  font-size: 0.75rem;
}

.neuroglancer-screenshot-scale-radio-input {
  margin: 0 0.188rem 0 0;
  display: inline-block;
  cursor: pointer;
}

.neuroglancer-screenshot-warning {
  color: red;
  width: auto;
  font-size: 0.75rem;
  margin: 0.12rem 0 0 -1.25rem;
}

/* Slice FOV fixed selection menu */
.neuroglancer-screenshot-keep-slice-fov-checkbox {
  margin: 0;
}

.neuroglancer-screenshot-keep-slice-label {
  display: flex;
  flex-direction: row-reverse;
  margin: 1rem 0;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

/* Take screenshot and close buttons */
.neuroglancer-screenshot-button {
  cursor: pointer;
  margin: 2px 0px;
  padding: 0;
}

.neuroglancer-screenshot-close-button {
  margin-left: auto;
  background-color: transparent;
  border: 0;
}

/* Screenshot resolution table - voxel resolution, panel resolution */
.neuroglancer-screenshot-size-text {
  margin: 0.75rem 0 0.75rem 0;
  display: flex;
  align-items: center;
}

.neuroglancer-screenshot-resolution-size-label {
  text-align: left;
  color: var(--gray200);
  font-size: 0.813rem;
  font-style: normal;
  font-weight: 590;
  width: 33.33%;
  padding: 0;
  margin: 0 0;
}

.neuroglancer-screenshot-resolution-size-value {
  font-size: 0.813rem;
  color: var(--gray700);
  width: 33.33%;
  padding: 0.25rem;
  font-weight: 400;
}

.neuroglancer-screenshot-resolution-preview-container {
  border-top: 1px solid var(--gray50);
  border-bottom: 1px solid var(--gray50);
  background: var(--gray500);
  width: 100%;
  padding: 1rem 1rem 0.5rem 1rem;
}

.neuroglancer-screenshot-resolution-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  padding: 4px 2px 2px 2px;
  background-color: var(--gray50);
  margin-bottom: 0.75rem;
  border-radius: 0.35rem;
  border: 0;
}

.neuroglancer-screenshot-resolution-table th {
  font-size: 0.813rem;
  width: 33.33%;
  text-align: left;
  color: var(--gray200);
  font-style: normal;
  font-weight: 590;
  background: var(--gray50);
  padding: 0.25rem 0.375rem;
}

.neuroglancer-screenshot-resolution-table td {
  font-size: 0.813rem;
  width: 33.33%;
  color: var(--gray700);
  background: white;
  padding: 0.375rem;
  line-height: 1.25rem;
}

.neuroglancer-screenshot-resolution-table-tooltip {
  vertical-align: top;
  margin-left: 0.25rem;
}

.neuroglancer-screenshot-copy-icon {
  outline: 0;
  border: 0;
  cursor: pointer;
  height: 1rem;
  margin-left: auto;
  position: relative;
  width: 33.33%;
  justify-content: flex-end;
}

.neuroglancer-screenshot-dimension {
  color: var(--gray600);
}

/* Screenshot statistics table - shows GPU memory etc. */
.neuroglancer-screenshot-statistics-table {
  width: 100%;
  border-collapse: collapse;
}

.neuroglancer-screenshot-statistics-table th {
  text-align: left;
  width: 33.33%;
  padding: 0 0 0.5rem;
}

.neuroglancer-screenshot-statistics-table td {
  text-align: left;
  width: 33.33%;
  font-size: 0.813rem;
  padding: 0.5rem 0;
}

.neuroglancer-screenshot-statistics-table-data-key {
  color: var(--gray200);
  font-weight: 590;
}

.neuroglancer-screenshot-statistics-table-data-value {
  color: var(--gray700);
  font-weight: 400;
}

.neuroglancer-statistics-table-description-header {
  font-size: 0.813rem;
  color: var(--gray700);
  line-height: 1.25rem;
  font-weight: 400;
}

.neuroglancer-statistics-table-description-link {
  font-weight: 590;
  cursor: pointer;
  color: var(--primary500);
  margin-left: 0.1rem;
}

/* Icons in the dialog */
.neuroglancer-screenshot-dialog .neuroglancer-icon svg {
  stroke: rgba(20, 20, 21, 0.4);
  width: 1rem;
  height: 1rem;
}

.neuroglancer-screenshot-dialog .neuroglancer-icon {
  width: 1rem;
  height: 1rem;
  min-width: inherit;
  min-height: inherit;
  padding: 0;
}

.neuroglancer-screenshot-dialog .neuroglancer-icon:hover {
  background: none;
}

/* Footer with progress and buttons */
.neuroglancer-screenshot-footer-container {
  margin: 0;
  display: flex;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--gray50);
}

.neuroglancer-screenshot-progress-text {
  margin: 0;
  flex: 1;
  font-weight: 590;
  cursor: pointer;
  padding: 0;
  font-size: 0.813rem;
  align-self: center;
  color: var(--primary700);
}

.neuroglancer-screenshot-footer-button {
  border-radius: 0.5rem;
  border: 1px solid var(--gray300);
  background: white;
  padding: 0.5rem 0.75rem;
  font-size: 0.813rem;
  font-weight: 590;
  color: var(--gray800);
  margin: 0 0 0 0.25rem;
}

.neuroglancer-screenshot-footer-button:disabled {
  display: none;
}
`, "",{"version":3,"sources":["webpack://./node_modules/neuroglancer/lib/ui/screenshot_menu.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;EAcE;;AAEF,uCAAuC;AACvC;EACE,kBAAkB;EAClB,kBAAkB;EAClB,kBAAkB;EAClB,iBAAiB;EACjB,kBAAkB;EAClB,gCAAgC;EAChC,gCAAgC;EAChC,gCAAgC;EAChC,qBAAqB;EACrB,qBAAqB;AACvB;;AAEA,uDAAuD;AACvD;EACE,sBAAsB;EACtB,UAAU;AACZ;;AAEA;EACE,eAAe;EACf,UAAU;EACV,oBAAoB;EACpB,kBAAkB;EAClB,eAAe;EACf,UAAU;EACV,SAAS;EACT,qBAAqB;EACrB,uBAAuB;AACzB;;AAEA;EACE,YAAY;EACZ,+BAA+B;EAC/B,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,mBAAmB;EACnB,gBAAgB;EAChB,qBAAqB;EACrB,SAAS;AACX;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;AAChC;;AAEA;EACE,mBAAmB;EACnB,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA,4CAA4C;AAC5C;EACE,sCAAsC;EACtC,aAAa;EACb,mBAAmB;EACnB,qBAAqB;EACrB,SAAS;AACX;;AAEA,wBAAwB;AACxB;EACE,+BAA+B;AACjC;;AAEA;EACE,qBAAqB;EACrB,kBAAkB;EAClB,cAAc;EACd,0BAA0B;AAC5B;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,kBAAkB;EAClB,gCAAgC;EAChC,iBAAiB;EACjB,mBAAmB;EACnB,kBAAkB;EAClB,iBAAiB;EACjB,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;EAChB,qBAAqB;EACrB,kDAAkD;EAClD,iBAAiB;EACjB,sBAAsB;EACtB,UAAU;AACZ;;AAEA;EACE,0BAA0B;EAC1B,qBAAqB;AACvB;;AAEA;EACE,qBAAqB;AACvB;;AAEA,yBAAyB;AACzB;EACE,WAAW;EACX,qBAAqB;EACrB,aAAa;EACb,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,eAAe;EACf,aAAa;EACb,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,sBAAsB;AACxB;;AAEA;EACE,qBAAqB;EACrB,kBAAkB;AACpB;;AAEA;EACE,sBAAsB;EACtB,qBAAqB;EACrB,eAAe;AACjB;;AAEA;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA,mCAAmC;AACnC;EACE,SAAS;AACX;;AAEA;EACE,aAAa;EACb,2BAA2B;EAC3B,cAAc;EACd,WAAW;EACX,yBAAyB;EACzB,mBAAmB;EACnB,WAAW;AACb;;AAEA,sCAAsC;AACtC;EACE,eAAe;EACf,eAAe;EACf,UAAU;AACZ;;AAEA;EACE,iBAAiB;EACjB,6BAA6B;EAC7B,SAAS;AACX;;AAEA,qEAAqE;AACrE;EACE,2BAA2B;EAC3B,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,qBAAqB;EACrB,mBAAmB;EACnB,kBAAkB;EAClB,gBAAgB;EAChB,aAAa;EACb,UAAU;EACV,WAAW;AACb;;AAEA;EACE,mBAAmB;EACnB,qBAAqB;EACrB,aAAa;EACb,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,mCAAmC;EACnC,sCAAsC;EACtC,0BAA0B;EAC1B,WAAW;EACX,8BAA8B;AAChC;;AAEA;EACE,WAAW;EACX,yBAAyB;EACzB,iBAAiB;EACjB,wBAAwB;EACxB,+BAA+B;EAC/B,sBAAsB;EACtB,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,mBAAmB;EACnB,aAAa;EACb,gBAAgB;EAChB,qBAAqB;EACrB,kBAAkB;EAClB,gBAAgB;EAChB,yBAAyB;EACzB,yBAAyB;AAC3B;;AAEA;EACE,mBAAmB;EACnB,aAAa;EACb,qBAAqB;EACrB,iBAAiB;EACjB,iBAAiB;EACjB,oBAAoB;AACtB;;AAEA;EACE,mBAAmB;EACnB,oBAAoB;AACtB;;AAEA;EACE,UAAU;EACV,SAAS;EACT,eAAe;EACf,YAAY;EACZ,iBAAiB;EACjB,kBAAkB;EAClB,aAAa;EACb,yBAAyB;AAC3B;;AAEA;EACE,qBAAqB;AACvB;;AAEA,wDAAwD;AACxD;EACE,WAAW;EACX,yBAAyB;AAC3B;;AAEA;EACE,gBAAgB;EAChB,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,aAAa;EACb,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA;EACE,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA;EACE,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA;EACE,mBAAmB;EACnB,qBAAqB;EACrB,oBAAoB;EACpB,gBAAgB;AAClB;;AAEA;EACE,gBAAgB;EAChB,eAAe;EACf,wBAAwB;EACxB,mBAAmB;AACrB;;AAEA,wBAAwB;AACxB;EACE,6BAA6B;EAC7B,WAAW;EACX,YAAY;AACd;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,mBAAmB;EACnB,UAAU;AACZ;;AAEA;EACE,gBAAgB;AAClB;;AAEA,qCAAqC;AACrC;EACE,SAAS;EACT,aAAa;EACb,qBAAqB;EACrB,mCAAmC;AACrC;;AAEA;EACE,SAAS;EACT,OAAO;EACP,gBAAgB;EAChB,eAAe;EACf,UAAU;EACV,mBAAmB;EACnB,kBAAkB;EAClB,wBAAwB;AAC1B;;AAEA;EACE,qBAAqB;EACrB,gCAAgC;EAChC,iBAAiB;EACjB,uBAAuB;EACvB,mBAAmB;EACnB,gBAAgB;EAChB,qBAAqB;EACrB,qBAAqB;AACvB;;AAEA;EACE,aAAa;AACf","sourcesContent":["/**\n * @license\n * Copyright 2024 Google Inc.\n * Licensed under the Apache License, Version 2.0 (the \"License\");\n * you may not use this file except in compliance with the License.\n * You may obtain a copy of the License at\n *\n *      http://www.apache.org/licenses/LICENSE-2.0\n *\n * Unless required by applicable law or agreed to in writing, software\n * distributed under the License is distributed on an \"AS IS\" BASIS,\n * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.\n * See the License for the specific language governing permissions and\n * limitations under the License.\n */\n\n/* Color variables for the screenshot */\n.neuroglancer-screenshot-overlay {\n  --gray300: #d0d5dd;\n  --gray600: #141415;\n  --gray500: #f7f7f7;\n  --gray50: #e6e6e6;\n  --gray800: #344054;\n  --gray700: rgba(20, 20, 21, 0.6);\n  --gray400: rgba(20, 20, 21, 0.4);\n  --gray200: rgba(20, 20, 21, 0.8);\n  --primary500: #0069eb;\n  --primary700: #0474ff;\n}\n\n/* General headings, labels, and top-level containers */\n.neuroglancer-screenshot-overlay :is(div, table, tr, td, th) {\n  box-sizing: border-box;\n  outline: 0;\n}\n\n.neuroglancer-screenshot-dialog {\n  width: 48.75rem;\n  padding: 0;\n  margin: 1.25rem auto;\n  position: relative;\n  transform: none;\n  left: auto;\n  top: auto;\n  border-radius: 0.5rem;\n  font-family: sans-serif;\n}\n\n.neuroglancer-screenshot-main-body-container {\n  height: auto;\n  max-height: calc(100vh - 200px);\n  overflow-y: auto;\n  overflow-x: hidden;\n}\n\n.neuroglancer-screenshot-title {\n  font-size: 0.938rem;\n  font-weight: 590;\n  color: var(--gray600);\n  margin: 0;\n}\n\n.neuroglancer-screenshot-title-subheading {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n}\n\n.neuroglancer-screenshot-label {\n  font-size: 0.813rem;\n  color: var(--gray200);\n  font-weight: 590;\n}\n\n/* Div at the top which contains the close */\n.neuroglancer-screenshot-close {\n  border-bottom: 1px solid var(--gray50);\n  display: flex;\n  align-items: center;\n  padding: 0.75rem 1rem;\n  gap: 10px;\n}\n\n/* Filename input menu */\n.neuroglancer-screenshot-filename-container {\n  padding: 1rem 1rem 0.75rem 1rem;\n}\n\n.neuroglancer-screenshot-name-label {\n  color: var(--gray800);\n  font-style: normal;\n  display: block;\n  margin: 0.75rem 0 0.375rem;\n}\n\n.neuroglancer-screenshot-name-input {\n  width: 100%;\n  margin-right: 10px;\n  border-radius: 8px;\n  border: 1px solid var(--gray300);\n  background: white;\n  font-size: 0.813rem;\n  font-style: normal;\n  padding: 8px 12px;\n  align-items: center;\n  gap: 8px;\n  font-weight: 400;\n  color: var(--gray700);\n  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);\n  line-height: 20px;\n  box-sizing: border-box;\n  outline: 0;\n}\n\n.neuroglancer-screenshot-name-input:disabled {\n  background: var(--gray500);\n  color: var(--gray400);\n}\n\n.neuroglancer-screenshot-name-input::placeholder {\n  color: var(--gray700);\n}\n\n/* Scale selection menu */\n.neuroglancer-screenshot-scale-factor-label {\n  width: 100%;\n  margin-bottom: 0.5rem;\n  display: flex;\n  align-items: center;\n  gap: 0.15rem;\n}\n\n.neuroglancer-screenshot-scale-menu {\n  padding: 0 1rem;\n  display: flex;\n  flex-wrap: wrap;\n}\n\n.neuroglancer-screenshot-scale-radio-container {\n  display: flex;\n  flex-direction: row;\n}\n\n.neuroglancer-screenshot-scale-radio-item {\n  margin-right: 2.125rem;\n}\n\n.neuroglancer-screenshot-scale-radio-label {\n  color: var(--gray700);\n  font-size: 0.75rem;\n}\n\n.neuroglancer-screenshot-scale-radio-input {\n  margin: 0 0.188rem 0 0;\n  display: inline-block;\n  cursor: pointer;\n}\n\n.neuroglancer-screenshot-warning {\n  color: red;\n  width: auto;\n  font-size: 0.75rem;\n  margin: 0.12rem 0 0 -1.25rem;\n}\n\n/* Slice FOV fixed selection menu */\n.neuroglancer-screenshot-keep-slice-fov-checkbox {\n  margin: 0;\n}\n\n.neuroglancer-screenshot-keep-slice-label {\n  display: flex;\n  flex-direction: row-reverse;\n  margin: 1rem 0;\n  width: 100%;\n  justify-content: flex-end;\n  align-items: center;\n  gap: 0.5rem;\n}\n\n/* Take screenshot and close buttons */\n.neuroglancer-screenshot-button {\n  cursor: pointer;\n  margin: 2px 0px;\n  padding: 0;\n}\n\n.neuroglancer-screenshot-close-button {\n  margin-left: auto;\n  background-color: transparent;\n  border: 0;\n}\n\n/* Screenshot resolution table - voxel resolution, panel resolution */\n.neuroglancer-screenshot-size-text {\n  margin: 0.75rem 0 0.75rem 0;\n  display: flex;\n  align-items: center;\n}\n\n.neuroglancer-screenshot-resolution-size-label {\n  text-align: left;\n  color: var(--gray200);\n  font-size: 0.813rem;\n  font-style: normal;\n  font-weight: 590;\n  width: 33.33%;\n  padding: 0;\n  margin: 0 0;\n}\n\n.neuroglancer-screenshot-resolution-size-value {\n  font-size: 0.813rem;\n  color: var(--gray700);\n  width: 33.33%;\n  padding: 0.25rem;\n  font-weight: 400;\n}\n\n.neuroglancer-screenshot-resolution-preview-container {\n  border-top: 1px solid var(--gray50);\n  border-bottom: 1px solid var(--gray50);\n  background: var(--gray500);\n  width: 100%;\n  padding: 1rem 1rem 0.5rem 1rem;\n}\n\n.neuroglancer-screenshot-resolution-table {\n  width: 100%;\n  border-collapse: separate;\n  border-spacing: 0;\n  padding: 4px 2px 2px 2px;\n  background-color: var(--gray50);\n  margin-bottom: 0.75rem;\n  border-radius: 0.35rem;\n  border: 0;\n}\n\n.neuroglancer-screenshot-resolution-table th {\n  font-size: 0.813rem;\n  width: 33.33%;\n  text-align: left;\n  color: var(--gray200);\n  font-style: normal;\n  font-weight: 590;\n  background: var(--gray50);\n  padding: 0.25rem 0.375rem;\n}\n\n.neuroglancer-screenshot-resolution-table td {\n  font-size: 0.813rem;\n  width: 33.33%;\n  color: var(--gray700);\n  background: white;\n  padding: 0.375rem;\n  line-height: 1.25rem;\n}\n\n.neuroglancer-screenshot-resolution-table-tooltip {\n  vertical-align: top;\n  margin-left: 0.25rem;\n}\n\n.neuroglancer-screenshot-copy-icon {\n  outline: 0;\n  border: 0;\n  cursor: pointer;\n  height: 1rem;\n  margin-left: auto;\n  position: relative;\n  width: 33.33%;\n  justify-content: flex-end;\n}\n\n.neuroglancer-screenshot-dimension {\n  color: var(--gray600);\n}\n\n/* Screenshot statistics table - shows GPU memory etc. */\n.neuroglancer-screenshot-statistics-table {\n  width: 100%;\n  border-collapse: collapse;\n}\n\n.neuroglancer-screenshot-statistics-table th {\n  text-align: left;\n  width: 33.33%;\n  padding: 0 0 0.5rem;\n}\n\n.neuroglancer-screenshot-statistics-table td {\n  text-align: left;\n  width: 33.33%;\n  font-size: 0.813rem;\n  padding: 0.5rem 0;\n}\n\n.neuroglancer-screenshot-statistics-table-data-key {\n  color: var(--gray200);\n  font-weight: 590;\n}\n\n.neuroglancer-screenshot-statistics-table-data-value {\n  color: var(--gray700);\n  font-weight: 400;\n}\n\n.neuroglancer-statistics-table-description-header {\n  font-size: 0.813rem;\n  color: var(--gray700);\n  line-height: 1.25rem;\n  font-weight: 400;\n}\n\n.neuroglancer-statistics-table-description-link {\n  font-weight: 590;\n  cursor: pointer;\n  color: var(--primary500);\n  margin-left: 0.1rem;\n}\n\n/* Icons in the dialog */\n.neuroglancer-screenshot-dialog .neuroglancer-icon svg {\n  stroke: rgba(20, 20, 21, 0.4);\n  width: 1rem;\n  height: 1rem;\n}\n\n.neuroglancer-screenshot-dialog .neuroglancer-icon {\n  width: 1rem;\n  height: 1rem;\n  min-width: inherit;\n  min-height: inherit;\n  padding: 0;\n}\n\n.neuroglancer-screenshot-dialog .neuroglancer-icon:hover {\n  background: none;\n}\n\n/* Footer with progress and buttons */\n.neuroglancer-screenshot-footer-container {\n  margin: 0;\n  display: flex;\n  padding: 0.75rem 1rem;\n  border-top: 1px solid var(--gray50);\n}\n\n.neuroglancer-screenshot-progress-text {\n  margin: 0;\n  flex: 1;\n  font-weight: 590;\n  cursor: pointer;\n  padding: 0;\n  font-size: 0.813rem;\n  align-self: center;\n  color: var(--primary700);\n}\n\n.neuroglancer-screenshot-footer-button {\n  border-radius: 0.5rem;\n  border: 1px solid var(--gray300);\n  background: white;\n  padding: 0.5rem 0.75rem;\n  font-size: 0.813rem;\n  font-weight: 590;\n  color: var(--gray800);\n  margin: 0 0 0 0.25rem;\n}\n\n.neuroglancer-screenshot-footer-button:disabled {\n  display: none;\n}\n"],"sourceRoot":""}]);
// Exports
export default ___CSS_LOADER_EXPORT___;
